import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { login, changePassword } from "../api/authApi";
import { jwtDecode } from "jwt-decode";
export const useAuth = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Fetch auth status from localStorage
  const { data: authData = { isAuthenticated: false, role: "" }, isLoading } =
    useQuery({
      queryKey: ["authStatus"],
      queryFn: () => {
        const token = localStorage.getItem("token");
        if (!token) return { isAuthenticated: false, role: "" };

        try {
          const decoded = jwtDecode(token);
          return { isAuthenticated: true, role: decoded.role };
        } catch (error) {
          return { isAuthenticated: false, role: "" };
        }
      },
    });

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: (data) => {
      toast.success(data.message || "Login successful!");
      localStorage.setItem("token", data.token);

      const decoded = jwtDecode(data.token);
      queryClient.setQueryData(["authStatus"], {
        isAuthenticated: true,
        role: decoded.role,
        communityId: decoded.communityId || 1,
      });
      // Which community's site is shown is determined by the DOMAIN (resolved
      // at app startup into localStorage.communityId), NOT by the user's home
      // community. Do not override it with the JWT's community_id — a
      // multi-community agent/admin (e.g. the Lead Agent of Toscana whose home
      // community is YARCOBR) must keep viewing the community whose domain they
      // logged in on. Only seed it as a fallback if the domain never resolved one.
      if (decoded.communityId && !localStorage.getItem("communityId")) {
        localStorage.setItem("communityId", decoded.communityId.toString());
      }

      // Default tabs: Super Admin → Home (dashboard), Project Admin → Home, Agent → Listings
      setTimeout(() => {
        if (decoded.role === "SuperAdmin") {
          navigate("/super-admin-dashboard");
        } else if (decoded.role === "Admin" || decoded.role === "Manager") {
          navigate("/project-admin");
        } else if (decoded.role === "Agent") {
          navigate("/agent-admin");
        } else if (decoded.role === "public") {
          navigate("/user-dashboard");
        } else {
          navigate("/");
        }
      }, 500);
    },
    onError: (error) => {
      toast.error(error.response?.data.message || "Login failed!");
    },
  });

  const passwordChangeMutation = useMutation({
    mutationFn: changePassword,
    onSuccess: (data) => {
      toast.success(data.message || "Password changed successfully!");
      localStorage.setItem("token", data.token);
      const decoded = jwtDecode(data.token);
      queryClient.setQueryData(["authStatus"], {
        isAuthenticated: true,
        role: decoded.role,
      });
    },
    onError: (error) => {
      toast.error(error.response?.data.message || "Password change failed!");
    },
  });

  // Logout function
  const logout = () => {
    localStorage.removeItem("token");
    queryClient.setQueryData(["authStatus"], {
      isAuthenticated: false,
      role: "",
    });
    navigate("/");
  };

  return {
    isLoading,
    isAuthenticated: authData.isAuthenticated,
    role: authData.role,
    login: loginMutation.mutate,
    loginLoading: loginMutation.isPending,
    loginError: loginMutation.error,
    changePassword: passwordChangeMutation.mutateAsync,
    logout,
  };
};
